/**
 * useBadgeCelebrations — Phase MP-9A / MP-9C
 *
 * Drives toast + modal celebrations for newly-earned badges.
 *
 * Two ingestion modes:
 *   • `ingest(badgesPayload)` — the /api/member/badges shape, where
 *     `newly_awarded` is the full list of badge VIEWS and `badges` is
 *     the catalog used to enrich them if needed.
 *   • `ingestViews(viewsArray)` — Phase MP-9C event-driven path. Any
 *     action endpoint (poll vote, discussion create, mock draft, money
 *     pot approval) can feed the hook its `newly_awarded_badges` array
 *     directly so celebrations fire immediately without re-loading
 *     /api/member/badges.
 *
 * Both paths dedupe via localStorage so closing/reopening the app
 * never re-fires stale celebrations.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { RARITY_LABEL, getCelebrationStyle } from '../lib/badgeStyles';

const STORAGE_KEY = 'fed.celebrated_badge_ids.v1';

const readSeen = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch { return new Set(); }
};

const writeSeen = (set) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(set))); }
  catch { /* ignore quota */ }
};

// Normalize one entry from `newly_awarded` — accepts a full view object
// OR a bare badge_id string + a catalog to enrich from. Returns the
// view shape the modal expects, or null if it cannot be resolved.
const toView = (entry, allBadges = []) => {
  if (!entry) return null;
  if (typeof entry === 'string') {
    return allBadges.find(b => b.badge_id === entry) || null;
  }
  if (typeof entry === 'object' && entry.badge_id) {
    // Already a view; merge any missing presentation fields from the
    // catalog as a defensive fallback.
    const fromCatalog = allBadges.find(b => b.badge_id === entry.badge_id);
    return { ...(fromCatalog || {}), ...entry };
  }
  return null;
};

const useBadgeCelebrations = () => {
  const [queue, setQueue]   = useState([]);
  const [open, setOpen]     = useState(false);
  const seenRef             = useRef(readSeen());

  const _enqueueViews = useCallback((views) => {
    const fresh = [];
    for (const v of views) {
      if (!v || !v.badge_id) continue;
      if (seenRef.current.has(v.badge_id)) continue;
      fresh.push(v);
      seenRef.current.add(v.badge_id);
    }
    if (fresh.length === 0) return;
    writeSeen(seenRef.current);

    fresh.forEach((b) => {
      const rarityLabel = RARITY_LABEL[b.badge_rarity] || 'Badge';
      const celeb = getCelebrationStyle(b.badge_rarity);
      toast.success(`${rarityLabel} Badge Unlocked`, {
        description: b.badge_name,
        duration: 5000,
        classNames: {
          toast:       celeb.toastClass,
          title:       celeb.toastTitle,
          description: celeb.toastDesc,
          actionButton: celeb.toastAction,
        },
        action: {
          label: 'View',
          onClick: () => {
            // Route only if the app hasn't already unmounted the page.
            if (typeof window !== 'undefined') {
              window.location.assign('/member/badges');
            }
          },
        },
      });
    });

    setQueue(prev => [...prev, ...fresh]);
    setOpen(true);
  }, []);

  // /api/member/badges payload path.
  const ingest = useCallback((badgesPayload) => {
    if (!badgesPayload) return;
    const allBadges = badgesPayload.badges || [];
    const newly = badgesPayload.newly_awarded || [];
    if (newly.length === 0) return;
    const views = newly
      .map(entry => toView(entry, allBadges))
      .filter(Boolean);
    _enqueueViews(views);
  }, [_enqueueViews]);

  // Direct views path (MP-9C) — any endpoint can call this with the
  // `newly_awarded_badges` array from its response.
  const ingestViews = useCallback((views) => {
    if (!views || !views.length) return;
    _enqueueViews(views.map(v => toView(v)).filter(Boolean));
  }, [_enqueueViews]);

  const close = useCallback(() => {
    setOpen(false);
    setTimeout(() => setQueue([]), 250);
  }, []);

  // First-time hydrate from initial badges payload should not re-celebrate
  // already-earned badges. Caller is expected to call `prime(initialPayload)`
  // exactly once on first successful response so the seen set absorbs them.
  const prime = useCallback((badgesPayload) => {
    if (!badgesPayload) return;
    const earned = (badgesPayload.badges || [])
      .filter(b => b.status === 'earned')
      .map(b => b.badge_id);
    let dirty = false;
    for (const id of earned) {
      if (!seenRef.current.has(id)) {
        seenRef.current.add(id);
        dirty = true;
      }
    }
    if (dirty) writeSeen(seenRef.current);
  }, []);

  return { queue, open, ingest, ingestViews, prime, close };
};

export default useBadgeCelebrations;
